import { Tabs } from 'antd';
import { css, cx } from '@emotion/css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { OrderList } from './OrderList';
import { OrderListSearchParams, useOrderList } from 'hooks/business/chat/useOrderList';
import { useTicket } from 'hooks/business/chat/useTicket';
import { Ticket } from './Ticket';
import { useTradeOverThreeMonthEnable } from 'hooks/business/useOrgConfig';
import { useTranslate } from '@tolgee/react';
import { useMemoizedFn } from 'ahooks';

interface SideMainProps {
  conversation: Conversation.Conversation;
}

const TabTitle = ({ title, total }: { title: string; total: number }) => {
  return (
    <div>
      {title}
      <span className="ml-1 text-content-200 font-normal">{total}</span>
    </div>
  );
};

interface TabsTotal {
  orders: number;
  tickets: number;
}

const ORDER_PAGE_SIZE = 10;

export const SideMain = (props: SideMainProps) => {
  const { conversation } = props;
  const { storeId, buyerId, tunnelAccountId, channel, externalBuyerId } = conversation;
  const { t } = useTranslate();

  const [activeTab, setActiveTab] = useState<'orders' | 'tickets'>('orders');
  const [tabsTotal, setTabsTotal] = useState<TabsTotal>({
    orders: 0,
    tickets: 0,
  });

  const handleTabChange = useCallback((key: string) => {
    setActiveTab(key as 'orders' | 'tickets');
  }, []);

  const orderListContainerRef = useRef<HTMLDivElement>(null);

  const handleScrollToOrder = useMemoizedFn((externalTradeId: string) => {
    // 先在当前列表中查找
    const container = orderListContainerRef.current;
    if (container) {
      const targetEl = container.querySelector(
        `[data-external-trade-id="${externalTradeId}"]`,
      ) as HTMLElement | null;
      if (targetEl) {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    // 未找到则通过搜索来定位
    setSearchParams({ external_trade_ids: externalTradeId });
  });

  const TRADE_OVER_THREE_MONTH = useTradeOverThreeMonthEnable();
  const isShopeeNotSupportOverThreeMonth = channel === 'SHOPEE' && !TRADE_OVER_THREE_MONTH;

  /** 订单相关模块 */
  const [searchParams, setSearchParams] = useState<OrderListSearchParams | undefined>();

  const orderProps = useOrderList({
    buyerId,
    storeId,
    tunnelAccountId,
    channel,
    pageSize: ORDER_PAGE_SIZE,
    searchParams,
  });
  const orderTotal = orderProps.total;

  /** 工单相关模块 */
  const ticketProps = useTicket({
    buyerId,
    storeId,
    tradeBuyerId: externalBuyerId,
  });
  const ticketTotal = ticketProps.total;

  const orderTotalDisplay = useMemo(() => {
    if (isShopeeNotSupportOverThreeMonth && orderProps?.orderList?.length === 0) {
      return 0;
    }

    return orderTotal;
  }, [isShopeeNotSupportOverThreeMonth, orderProps?.orderList?.length, orderTotal]);

  useEffect(() => {
    setTabsTotal((prev) => ({ ...prev, orders: orderTotal }));
  }, [orderTotal]);

  useEffect(() => {
    setTabsTotal((prev) => ({ ...prev, tickets: ticketTotal }));
  }, [ticketTotal]);

  return (
    <div
      className={cx('mt-4 flex flex-col overflow-hidden h-full', containerCls)}
      ref={orderListContainerRef}
    >
      <Tabs
        className={cx(tabsStyle, 'flex-shrink-0')}
        activeKey={activeTab}
        onChange={handleTabChange}
      >
        <Tabs.TabPane
          tab={<TabTitle title={t('tab.trade')} total={orderTotalDisplay} />}
          key="orders"
        />
        <Tabs.TabPane
          tab={<TabTitle title={t('tab.todo')} total={tabsTotal.tickets} />}
          key="tickets"
        />
      </Tabs>
      {activeTab === 'orders' && (
        <OrderList
          isShopeeNotSupportOverThreeMonth={isShopeeNotSupportOverThreeMonth}
          conversation={conversation}
          searchParams={searchParams}
          onSearchParamsChange={setSearchParams}
          onScrollToOrder={handleScrollToOrder}
          {...orderProps}
        />
      )}
      {activeTab === 'tickets' && <Ticket conversation={conversation} {...ticketProps} />}
    </div>
  );
};

const tabsStyle = css`
  .ant-tabs-nav {
    margin-bottom: 0;
    padding: 0 12px;
    background: var(--fett-bg-panel);
  }

  .ant-tabs-tab.ant-tabs-tab-active .ant-tabs-tab-btn {
    color: var(--fett-text-primary);
    font-weight: 500;
  }

  .ant-tabs-tab:hover,
  .ant-tabs-tab-btn:active,
  .ant-tabs-tab-btn:focus,
  .ant-tabs-tab-remove:active,
  .ant-tabs-tab-remove:focus {
    color: var(--fett-text-primary) !important;
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 22px !important;
  }
`;

const containerCls = css`
  .ant-btn:not(.ant-btn-link) {
    font-weight: 500 !important;
  }
`;
